import React, { useCallback, useEffect, useState } from "react";

// =============================================================================
// DAY RANGE STRIP — Year tab day-of-month slicer (P11)
// =============================================================================
// Horizontal row of 31 number cells. Click a cell = single-day. Click-and-drag
// across cells = range. Replaces the two number inputs originally shipped with
// P11 (Mako: "as easy as either clicking a number or i can just left click and
// drag to the days of the month that i want").
//
// Pointer model: down sets the anchor; enter on any cell while held updates the
// preview; up commits min/max(anchor, final). releasePointerCapture lets
// sibling cells receive pointerenter (default capture would route all moves to
// the down cell). touchAction: 'none' on each cell suppresses scroll on touch
// drag so the gesture stays a range select.
// =============================================================================

interface DayRangeStripProps {
  dayStart: number;
  dayEnd: number;
  onChange: (next: { start: number; end: number }) => void;
}

const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

const DayRangeStrip: React.FC<DayRangeStripProps> = ({ dayStart, dayEnd, onChange }) => {
  const [drag, setDrag] = useState<{ anchor: number; preview: number } | null>(null);

  // While dragging, the strip shows the preview range; otherwise it shows committed state.
  const effStart = drag ? Math.min(drag.anchor, drag.preview) : dayStart;
  const effEnd = drag ? Math.max(drag.anchor, drag.preview) : dayEnd;

  const commit = useCallback(
    (anchor: number, latest: number) => {
      const s = Math.min(anchor, latest);
      const e = Math.max(anchor, latest);
      if (s !== dayStart || e !== dayEnd) onChange({ start: s, end: e });
      setDrag(null);
    },
    [dayStart, dayEnd, onChange]
  );

  // Window-level pointerup ensures release outside the strip still commits.
  // Without this, a drag that ends off-strip would leave drag state dangling.
  useEffect(() => {
    if (!drag) return;
    const onUp = () => commit(drag.anchor, drag.preview);
    const onCancel = () => setDrag(null);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    return () => {
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
    };
  }, [drag, commit]);

  // Whether the strip is in "all days" default state — endpoints touch both
  // outer edges. We DON'T endpoint-style days 1 and 31 in defaults because the
  // entire strip is the range, no need to mark caps.
  const isFullDefault = effStart === 1 && effEnd === 31;

  return (
    <div className="flex items-center gap-2 select-none" data-testid="year-tab-day-range">
      <span className="token-10 uppercase text-glass-muted tracking-[0.1em] shrink-0">Days</span>
      <div
        className="flex-1 flex items-center liquid-glass-light p-0.5 rounded-full"
        style={{ fontVariantNumeric: "tabular-nums" }}
        onDragStart={(e) => e.preventDefault()}
      >
        {DAYS.map((day) => {
          const inRange = day >= effStart && day <= effEnd;
          const isStartCap = !isFullDefault && day === effStart;
          const isEndCap = !isFullDefault && day === effEnd;
          const isSingle = effStart === effEnd && day === effStart;

          // Visual model mirrors DateRangePicker:
          //   single day        → solid blue pill, white text
          //   range endpoints   → solid blue, white text, half-pill rounding on outer edge
          //   range middle      → blue/30 fill, no rounding (continuous band)
          //   out of range      → muted text, hover: subtle dark overlay
          //   full-default 1-31 → all cells get the middle-band fill, no caps
          let bgClass = "";
          let textClass = "text-glass-muted";
          let radiusClass = "";

          if (isSingle) {
            bgClass = "bg-blue-500";
            textClass = "text-white";
            radiusClass = "rounded-full";
          } else if (isStartCap) {
            bgClass = "bg-blue-500";
            textClass = "text-white";
            radiusClass = "rounded-l-full";
          } else if (isEndCap) {
            bgClass = "bg-blue-500";
            textClass = "text-white";
            radiusClass = "rounded-r-full";
          } else if (inRange) {
            bgClass = "bg-blue-500/30";
            textClass = "text-glass";
            radiusClass = ""; // flat — adjacent in-range cells form a continuous bar
          } else {
            bgClass = "hover:bg-black/[0.06]";
            radiusClass = "rounded-full";
          }

          // Full-default state (1-31): treat every cell as "middle" — no caps, no
          // hard endpoints, the whole strip reads as "all days selected" without
          // visually shouting "you have a range applied."
          if (isFullDefault) {
            bgClass = "bg-blue-500/15";
            textClass = "text-glass";
            radiusClass = "";
          }

          return (
            <button
              key={day}
              type="button"
              data-day={day}
              data-in-range={inRange ? "true" : "false"}
              data-cap={isStartCap || isEndCap || isSingle ? "true" : "false"}
              aria-label={`Day ${day}`}
              aria-pressed={inRange}
              className={`flex-1 token-11 text-center py-1 transition-colors cursor-pointer ${bgClass} ${textClass} ${radiusClass} ${
                isStartCap || isEndCap || isSingle ? "font-semibold" : ""
              }`}
              style={{ touchAction: "none", minWidth: 0 }}
              onPointerDown={(e) => {
                (e.target as Element).releasePointerCapture?.(e.pointerId);
                setDrag({ anchor: day, preview: day });
              }}
              onPointerEnter={() => {
                setDrag((cur) => (cur && cur.preview !== day ? { ...cur, preview: day } : cur));
              }}
              onPointerUp={() => {
                if (drag) commit(drag.anchor, day);
              }}
            >
              {day}
            </button>
          );
        })}
      </div>
      {/* Toggle button. At full-default → "MTD" (sets days 1–today). Otherwise → */}
      {/* "Reset" (restores 1–31). `today` is user-local date; for non-+08 viewers */}
      {/* this can be off by 1 vs org tz late-night — acceptable for v1. */}
      <button
        type="button"
        onClick={() =>
          isFullDefault
            ? onChange({ start: 1, end: new Date().getDate() })
            : onChange({ start: 1, end: 31 })
        }
        aria-label={isFullDefault ? "Slice to month-to-date" : "Reset day range to all days"}
        title={isFullDefault ? `Slice to days 1–${new Date().getDate()}` : "Reset to all days"}
        data-testid="year-tab-day-range-reset"
        data-mode={isFullDefault ? "mtd" : "reset"}
        className="token-10 uppercase tracking-[0.1em] px-3 py-1.5 rounded-full transition-colors shrink-0 text-glass hover:bg-black/[0.06] cursor-pointer"
      >
        {isFullDefault ? "MTD" : "Reset"}
      </button>
    </div>
  );
};

export default DayRangeStrip;
